import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraOverlayDialog } from '@mono/ui-components/captains';
import { SpecraButton, SpecraInput, SpecraText, SpecraTextarea } from '@mono/ui-components/ensigns';
import { SpecraCombobox, SpecraDateField, SpecraFormField, SpecraOverlayFooter } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { useEffect, useState } from 'react';
import { RegistryEntityPicker } from '../../../../components/SharedRecord/RegistryEntityPicker';
import { DocumentPickerField } from '../../documents/DocumentPickerField';
import { CORPORATE_EVENT_OPTIONS, corporateEventOption, corporateEventSentence, linksForEvent, timestampToDay } from './corporateEvents';
import { useRecordCorporateEvent, useUpdateCorporateEvent } from './hooks';
const today = ()=>new Date().toISOString().slice(0, 10);
/**
 * Record, edit or end a corporate event (REG-62). Add picks the event and the
 * other company(ies) and writes one link per company; edit and end change
 * dates, notes, headcount and the source document of a stored link.
 */ export function CorporateEventDialog({ open, onOpenChange, companyId, companyName, mode }) {
    const record = useRecordCorporateEvent();
    const update = useUpdateCorporateEvent();
    const row = mode.kind === 'add' ? undefined : mode.row;
    const [event, setEvent] = useState(undefined);
    const [others, setOthers] = useState([]);
    const [effectiveDate, setEffectiveDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [notes, setNotes] = useState('');
    const [headcount, setHeadcount] = useState('');
    const [doc, setDoc] = useState(undefined);
    useEffect(()=>{
        if (!open) return;
        setEvent(undefined);
        setOthers([]);
        setDoc(undefined);
        setEffectiveDate(row ? timestampToDay(row.effectiveStart) : '');
        setEndDate(row ? mode.kind === 'end' ? today() : timestampToDay(row.effectiveEnd) : '');
        setNotes(row?.notes ?? '');
        setHeadcount(row?.transferredHeadcount !== undefined ? String(row.transferredHeadcount) : '');
    }, [
        open,
        row,
        mode.kind
    ]);
    const option = event ? corporateEventOption(event) : undefined;
    const headcountApplies = row ? row.relationshipType === 'acquisition' || row.relationshipType === 'merger' : !!option?.headcount;
    const parsedHeadcount = headcount.trim() === '' ? undefined : Number(headcount);
    const headcountInvalid = parsedHeadcount !== undefined && (!Number.isInteger(parsedHeadcount) || parsedHeadcount < 0);
    const datesInvalid = !!effectiveDate && !!endDate && endDate < effectiveDate;
    const fields = {
        effectiveDate,
        endDate,
        notes,
        sourceArtifactId: doc?.artifactId ?? row?.sourceArtifactId ?? '',
        transferredHeadcount: headcountApplies ? parsedHeadcount : undefined
    };
    const pending = record.isPending || update.isPending;
    const canSave = !pending && !headcountInvalid && !datesInvalid && (mode.kind === 'add' ? !!option && others.length > 0 : mode.kind === 'end' ? !!endDate : true);
    const save = ()=>{
        const close = {
            onSuccess: ()=>onOpenChange(false)
        };
        if (mode.kind === 'add' && event) {
            record.mutate({
                links: linksForEvent(event, companyId, others.map((o)=>o.id)),
                fields
            }, close);
        } else if (row) {
            update.mutate({
                row,
                fields
            }, close);
        }
    };
    const fromUs = row ? row.fromCompanyId === companyId : false;
    const title = mode.kind === 'add' ? 'Add corporate event' : mode.kind === 'end' ? 'End this link' : 'Edit corporate event';
    return /*#__PURE__*/ _jsxs(SpecraOverlayDialog, {
        open: open,
        onOpenChange: onOpenChange,
        size: "md",
        title: title,
        description: mode.kind === 'add' ? `What happened to ${companyName}, and with whom. Both companies' histories show it.` : corporateEventSentence(row?.relationshipType ?? '', fromUs, mode.otherName),
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "space-y-4",
                "data-testid": Testids.registryCompany.corporateEventDialog,
                children: [
                    mode.kind === 'add' && /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(SpecraCombobox, {
                                label: `${companyName}…`,
                                placeholder: "Choose what happened",
                                searchPlaceholder: "Search events",
                                emptyMessage: "No such event.",
                                value: event,
                                onValueChange: (v)=>{
                                    setEvent(v);
                                    setOthers([]);
                                },
                                options: CORPORATE_EVENT_OPTIONS.map((o)=>({
                                        value: o.value,
                                        label: o.label,
                                        textValue: o.label
                                    }))
                            }),
                            option && /*#__PURE__*/ _jsxs("div", {
                                className: "space-y-2",
                                children: [
                                    others.map((o)=>/*#__PURE__*/ _jsxs("div", {
                                            className: "flex items-center justify-between gap-2",
                                            children: [
                                                /*#__PURE__*/ _jsx(SpecraText, {
                                                    size: "body",
                                                    children: o.name
                                                }),
                                                /*#__PURE__*/ _jsx(SpecraButton, {
                                                    variant: "ghost",
                                                    size: "sm",
                                                    onClick: ()=>setOthers((xs)=>xs.filter((x)=>x.id !== o.id)),
                                                    children: "Remove"
                                                })
                                            ]
                                        }, o.id)),
                                    (option.multiple || others.length === 0) && /*#__PURE__*/ _jsx(RegistryEntityPicker, {
                                        kinds: [
                                            'COMPANY'
                                        ],
                                        label: option.otherLabel,
                                        placeholder: "Search the registry, or add a company",
                                        "data-testid": Testids.registryCompany.corporateEventCompany,
                                        onPick: (o)=>{
                                            if (o.registryId === companyId) return;
                                            setOthers((xs)=>xs.some((x)=>x.id === o.registryId) ? xs : [
                                                    ...xs,
                                                    {
                                                        id: o.registryId,
                                                        name: o.label
                                                    }
                                                ]);
                                        }
                                    })
                                ]
                            })
                        ]
                    }),
                    mode.kind !== 'end' && /*#__PURE__*/ _jsx(SpecraDateField, {
                        label: "Effective date",
                        hint: "Leave empty if you don't know it.",
                        value: effectiveDate,
                        onValueChange: setEffectiveDate
                    }),
                    /*#__PURE__*/ _jsx(SpecraDateField, {
                        label: mode.kind === 'end' ? 'Ended on' : 'End date',
                        hint: mode.kind === 'end' ? 'The link stays as history.' : 'Only if it has ended.',
                        value: endDate,
                        onValueChange: setEndDate,
                        error: datesInvalid ? 'The end date is before the effective date.' : undefined
                    }),
                    mode.kind !== 'end' && headcountApplies && /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "People who moved over",
                        htmlFor: "corporate-event-headcount",
                        error: headcountInvalid ? 'A whole number, 0 or more.' : undefined,
                        children: /*#__PURE__*/ _jsx(SpecraInput, {
                            id: "corporate-event-headcount",
                            type: "number",
                            min: 0,
                            inputMode: "numeric",
                            value: headcount,
                            invalid: headcountInvalid,
                            onChange: (e)=>setHeadcount(e.target.value)
                        })
                    }),
                    /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "Notes",
                        htmlFor: "corporate-event-notes",
                        hint: "e.g. the press release or filing",
                        children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                            id: "corporate-event-notes",
                            rows: 3,
                            value: notes,
                            onChange: (e)=>setNotes(e.target.value)
                        })
                    }),
                    mode.kind !== 'end' && /*#__PURE__*/ _jsx(DocumentPickerField, {
                        label: row?.sourceArtifactId ? 'Source document (replace)' : 'Source document',
                        value: doc,
                        onChange: setDoc
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(SpecraOverlayFooter, {
                confirmLabel: mode.kind === 'add' ? 'Record event' : mode.kind === 'end' ? 'End link' : 'Save',
                confirmDisabled: !canSave,
                loading: pending,
                onConfirm: save,
                onCancel: ()=>onOpenChange(false)
            })
        ]
    });
}
